import { LocalVideoTrack, Room } from 'twilio-video';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import guardBackgroundProcessor from './guardBackgroundProcessor';
import createBackgroundRenderer from './createBackgroundRenderer';
import { SELECTED_BACKGROUND_SETTINGS_KEY } from '../../../constants';
import {
	GaussianBlurBackgroundProcessor,
	VirtualBackgroundProcessor,
	ImageFit,
	isSupported,
} from '@twilio/video-processors';
import Abstract from '../../../images/Abstract.jpg';
import AbstractThumb from '../../../images/thumb/Abstract.jpg';
import BohoHome from '../../../images/BohoHome.jpg';
import BohoHomeThumb from '../../../images/thumb/BohoHome.jpg';
import Bookshelf from '../../../images/Bookshelf.jpg';
import BookshelfThumb from '../../../images/thumb/Bookshelf.jpg';
import CoffeeShop from '../../../images/CoffeeShop.jpg';
import CoffeeShopThumb from '../../../images/thumb/CoffeeShop.jpg';
import Contemporary from '../../../images/Contemporary.jpg';
import ContemporaryThumb from '../../../images/thumb/Contemporary.jpg';
import CozyHome from '../../../images/CozyHome.jpg';
import CozyHomeThumb from '../../../images/thumb/CozyHome.jpg';
import Desert from '../../../images/Desert.jpg';
import DesertThumb from '../../../images/thumb/Desert.jpg';
import Fishing from '../../../images/Fishing.jpg';
import FishingThumb from '../../../images/thumb/Fishing.jpg';
import Flower from '../../../images/Flower.jpg';
import FlowerThumb from '../../../images/thumb/Flower.jpg';
import Kitchen from '../../../images/Kitchen.jpg';
import KitchenThumb from '../../../images/thumb/Kitchen.jpg';
import ModernHome from '../../../images/ModernHome.jpg';
import ModernHomeThumb from '../../../images/thumb/ModernHome.jpg';
import Nature from '../../../images/Nature.jpg';
import NatureThumb from '../../../images/thumb/Nature.jpg';
import Ocean from '../../../images/Ocean.jpg';
import OceanThumb from '../../../images/thumb/Ocean.jpg';
import Patio from '../../../images/Patio.jpg';
import PatioThumb from '../../../images/thumb/Patio.jpg';
import Plant from '../../../images/Plant.jpg';
import PlantThumb from '../../../images/thumb/Plant.jpg';
import SanFrancisco from '../../../images/SanFrancisco.jpg';
import SanFranciscoThumb from '../../../images/thumb/SanFrancisco.jpg';
import { Thumbnail } from '../../BackgroundSelectionDialog/BackgroundThumbnail/BackgroundThumbnail';
import { useLocalStorageState } from '../../../hooks/useLocalStorageState/useLocalStorageState';

export interface BackgroundSettings {
	type: Thumbnail;
	index?: number;
}

const imageNames: string[] = [
	'Abstract',
	'Boho Home',
	'Bookshelf',
	'Coffee Shop',
	'Contemporary',
	'Cozy Home',
	'Desert',
	'Fishing',
	'Flower',
	'Kitchen',
	'Modern Home',
	'Nature',
	'Ocean',
	'Patio',
	'Plant',
	'San Francisco',
];

const images = [
	AbstractThumb,
	BohoHomeThumb,
	BookshelfThumb,
	CoffeeShopThumb,
	ContemporaryThumb,
	CozyHomeThumb,
	DesertThumb,
	FishingThumb,
	FlowerThumb,
	KitchenThumb,
	ModernHomeThumb,
	NatureThumb,
	OceanThumb,
	PatioThumb,
	PlantThumb,
	SanFranciscoThumb,
];

const rawImagePaths = [
	Abstract,
	BohoHome,
	Bookshelf,
	CoffeeShop,
	Contemporary,
	CozyHome,
	Desert,
	Fishing,
	Flower,
	Kitchen,
	ModernHome,
	Nature,
	Ocean,
	Patio,
	Plant,
	SanFrancisco,
];

const imageElements = new Map<number, HTMLImageElement>();

const getImage = (index: number): Promise<HTMLImageElement> => {
	return new Promise((resolve, reject) => {
		if (imageElements.has(index)) {
			return resolve(imageElements.get(index)!);
		}
		if (!Number.isInteger(index) || !rawImagePaths[index]) {
			return reject(new Error('Invalid background image'));
		}
		const img = new Image();
		img.onload = () => {
			imageElements.set(index, img);
			resolve(img);
		};
		img.onerror = reject;
		img.src = rawImagePaths[index];
	});
};

export const backgroundConfig = {
	imageNames,
	images,
};

const virtualBackgroundAssets = '/virtualbackground';
const MODEL_TIMEOUT_MS = 15000;

export default function useBackgroundSettings(
	videoTrack: LocalVideoTrack | undefined,
	activeRoom?: Room | null,
	onError?: (error: Error) => void,
) {
	const [backgroundSettings, setBackgroundSettings] =
		useLocalStorageState<BackgroundSettings>(
			SELECTED_BACKGROUND_SETTINGS_KEY,
			{ type: 'none', index: 0 },
		);
	// Cache the loading promise, never an instance whose model is still loading.
	// Instances belong to this provider, so separate calls cannot share frame state.
	const blur = useRef<Promise<GaussianBlurBackgroundProcessor>>();
	const replacement = useRef<Promise<VirtualBackgroundProcessor>>();
	const errorHandler = useRef(onError);
	errorHandler.current = onError;

	const [status, setStatus] = useState<
		'off' | 'loading' | 'ready' | 'failed'
	>('off');
	const [revision, setRevision] = useState(0);
	const readiness = useRef<{ track?: LocalVideoTrack; ready: boolean }>({
		ready: false,
	});
	const roomRef = useRef(activeRoom);
	roomRef.current = activeRoom;
	const pausedPublication = useRef<{ room: Room; track: LocalVideoTrack }>();
	const resumePublication = useCallback(() => {
		const paused = pausedPublication.current;
		pausedPublication.current = undefined;
		if (
			paused &&
			!paused.track.isStopped &&
			paused.room.state !== 'disconnected'
		) {
			void paused.room.localParticipant
				.publishTrack(paused.track, { priority: 'low' })
				.catch(() =>
					errorHandler.current?.(
						new Error(
							'Unable to resume your camera. Turn it off and on to try again.',
						),
					),
				);
		}
	}, []);
	const settingsRef = useRef(backgroundSettings);
	settingsRef.current = backgroundSettings;
	const refreshBackground = useCallback(() => {
		readiness.current = { ready: false };
		setRevision((value) => value + 1);
	}, []);
	const waitForBackground = useCallback(async (track: LocalVideoTrack) => {
		const started = Date.now();
		while (settingsRef.current.type !== 'none') {
			if (track.isStopped) throw new Error('Camera request cancelled.');
			if (readiness.current.track === track && readiness.current.ready)
				return;
			if (Date.now() - started > 20000)
				throw new Error(
					'Camera preparation timed out. Please try again.',
				);
			await new Promise((resolve) => setTimeout(resolve, 20));
		}
	}, []);
	useLayoutEffect(() => {
		if (!videoTrack || backgroundSettings.type === 'none') {
			readiness.current = { track: videoTrack, ready: true };
			setStatus('off');
			resumePublication();
			return;
		}
		readiness.current = { track: videoTrack, ready: false };
		setStatus('loading');
		const room = roomRef.current;
		if (
			room?.state === 'connected' &&
			room.localParticipant.unpublishTrack
		) {
			const publication =
				room.localParticipant.unpublishTrack(videoTrack);
			if (publication) {
				pausedPublication.current = { room, track: videoTrack };
				room.localParticipant.emit('trackUnpublished', publication);
			}
		}
		// twilio-video only runs a processor's frame loop while the track is
		// published or attached to an element. The publication is paused while
		// the effect prepares and the self-view unmounts with it, so keep a
		// hidden element attached for the life of the effect. Without it no
		// frame is ever processed, readiness never arrives and the camera
		// stays unpublished (a black tile) until the model timeout gives up.
		const keepAlive = document.createElement('video');
		keepAlive.muted = true;
		keepAlive.setAttribute('playsinline', '');
		videoTrack.attach(keepAlive);
		let cancelled = false;
		let failed = false;
		let guarded: ReturnType<typeof guardBackgroundProcessor> | undefined;
		let loadingTimer: ReturnType<typeof setTimeout> | undefined;
		const detach = () => {
			guarded?.cancel();
			if (guarded && videoTrack.processor === guarded.processor) {
				videoTrack.removeProcessor(guarded.processor);
			}
		};
		const fail = (error: unknown) => {
			if (cancelled || failed) return;
			failed = true;
			setStatus('failed');
			clearTimeout(loadingTimer);
			detach();
			// Failed processors may hold an unusable GPU context or worker.
			blur.current = undefined;
			replacement.current = undefined;
			setBackgroundSettings({ type: 'none', index: 0 });
			console.warn('Background effect unavailable', error);
			errorHandler.current?.(
				new Error(
					'Your background effect stopped working. Your normal camera is now showing.',
				),
			);
		};
		const apply = async () => {
			try {
				if (!isSupported)
					throw new Error('Background effects are not supported');
				let processor:
					| GaussianBlurBackgroundProcessor
					| VirtualBackgroundProcessor;
				if (backgroundSettings.type === 'blur') {
					if (!blur.current) {
						blur.current = (async () => {
							const instance =
								new GaussianBlurBackgroundProcessor({
									assetsPath: virtualBackgroundAssets,
								});
							await instance.loadModel();
							return instance;
						})();
					}
					processor = await blur.current;
				} else {
					const image = await getImage(backgroundSettings.index!);
					if (cancelled || failed) return;
					if (!replacement.current) {
						replacement.current = (async () => {
							const instance = new VirtualBackgroundProcessor({
								assetsPath: virtualBackgroundAssets,
								backgroundImage: image,
								fitType: ImageFit.Cover,
							});
							await instance.loadModel();
							return instance;
						})();
					}
					processor = await replacement.current;
					if (cancelled || failed) return;
					processor.backgroundImage = image;
				}
				if (cancelled || failed) return;
				guarded = guardBackgroundProcessor(
					createBackgroundRenderer(processor),
					fail,
					() => {
						if (!cancelled && !failed) {
							readiness.current = {
								track: videoTrack,
								ready: true,
							};
							setStatus('ready');
							clearTimeout(loadingTimer);
							resumePublication();
						}
					},
				);
				videoTrack.addProcessor(guarded.processor, {
					inputFrameBufferType: 'video',
					outputFrameBufferContextType: '2d',
				});
			} catch (error) {
				fail(error);
			}
		};
		loadingTimer = setTimeout(
			() => fail(new Error('Background model loading timed out')),
			MODEL_TIMEOUT_MS,
		);
		void apply();
		return () => {
			cancelled = true;
			clearTimeout(loadingTimer);
			detach();
			videoTrack.detach(keepAlive);
		};
	}, [
		backgroundSettings,
		videoTrack,
		revision,
		setBackgroundSettings,
		resumePublication,
	]);

	return [
		backgroundSettings,
		setBackgroundSettings,
		status,
		waitForBackground,
		refreshBackground,
	] as const;
}
